import React, { useCallback, useRef, useState } from "react";
import { Swiper as SwiperType } from "swiper/types";
import { Box, DialogTitle, Divider, Step, StepLabel, Stepper } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { InitUpsellPage } from "./InitUpsellPage";
import { UpsellAddTriggersStep } from "./UpsellAddTriggersStep";
import { TupleKeys } from "../../modernTS/TypeUtils";
import { UpsellAddPageStep } from "./UpsellAddPageStep";
import { SwiperStepperActions } from "../SwiperStepperActions";
import { useHookForm } from "mui-react-hook-form-plus";
import "swiper/css";
import { isEmpty, mergeWith } from "lodash";
import UpsellFinishStep from "./UpsellFinishStep";
import { UpsellForm, useCreateUpsellMutation } from "../../../../store/api/upsellApi";
import { useSalesareaCtx } from "../../modernTS/salesarea/SalesareaContext";
import { useDialog } from "../../modernTS/JUI/Dialogs";
import { toast } from "react-toastify";
import upsell from "../../../Vestigingen/Tabs/Upsell";

const steps = ["Name", "Add triggers", "Show upsell products", "Finishing touches"] as const;
type Indices = TupleKeys<typeof steps>;

export type SubForms = ReturnType<typeof useHookForm>[];

function formMerger(current, toUpdate) {
  if (isEmpty(toUpdate) && !isEmpty(current)) {
    return toUpdate;
  }
}

export function CreateUpsellDialog({ onCancel, onCreate }: { onCancel: () => void; onCreate?: () => void }) {
  const [data, setData] = useState<Partial<UpsellForm>>({
    upsell_variant: "upsell",
  });
  const { closeDialog } = useDialog();
  const [page, setPage] = useState<Indices>(0);
  const swiperApi = useRef<SwiperType>();
  const { id: salesarea_id } = useSalesareaCtx();

  const [createUpsell, {}] = useCreateUpsellMutation();

  const subFormData = useRef<SubForms>([]);
  const validateCurrentPage = useCallback(() => subFormData.current[page].trigger(), [page]);

  const goNext = useCallback(() => {
    const subFormValues = subFormData.current[page]?.getValues();
    if (subFormValues) {
      setData((accumulatedData) => mergeWith(accumulatedData, subFormValues, formMerger));
    }
    if (page < steps.length) {
      setPage((p) => (p + 1) as Indices);
      swiperApi.current!.slideNext();
    }
  }, [page]);
  const goPrevious = useCallback(() => {
    const subFormValues = subFormData.current[page]?.getValues();
    if (subFormValues) {
      setData((accumulatedData) => mergeWith(accumulatedData, subFormValues, formMerger));
    }
    if (page > 0) {
      setPage((p) => (p - 1) as Indices);
      swiperApi.current!.slidePrev();
    }
  }, [page]);

  return (
    <Box>
      <DialogTitle>
        Create a new Upsell
        <Stepper activeStep={page} alternativeLabel sx={{ my: 4 }}>
          {steps.map((step) => (
            <Step key={step}>
              <StepLabel>{step}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>
      <Divider sx={{ mb: 2 }} />
      <Swiper
        allowTouchMove={false}
        onSwiper={(swiper) => (swiperApi.current = swiper)}
        slidesPerView={1}
        style={{
          maxHeight: "50vh",
          overflowY: "auto",
        }}
      >
        <SwiperSlide>
          <InitUpsellPage stepIndex={0} subForms={subFormData} />
        </SwiperSlide>
        <SwiperSlide>
          <UpsellAddTriggersStep stepIndex={1} subForms={subFormData} />
        </SwiperSlide>
        <SwiperSlide>
          <UpsellAddPageStep stepIndex={2} subForms={subFormData} />
        </SwiperSlide>
        <SwiperSlide>
          <UpsellFinishStep upsell={data as UpsellForm} />
        </SwiperSlide>
      </Swiper>

      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <SwiperStepperActions
          cancel={onCancel}
          currentPage={page}
          isValid={validateCurrentPage}
          next={goNext}
          onSubmit={async () => {
            // Error handling should not be handled inline, too flimsy for all of the requests we'll be creating
            try {
              const answer = await createUpsell({
                upsell: data as UpsellForm,
                salesarea_id,
              }).unwrap();
              console.log(answer);
              if (answer) {
                toast.success(() => (
                  <p>
                    Upsell <span style={{ textDecoration: "underline" }}>{data.name}</span> has been saved successfully!
                  </p>
                ));
                closeDialog("add-new-upsell");
              } else {
                toast.error(() => (
                  <p>
                    Unable to save <span style={{ textDecoration: "underline" }}>{data.name}</span>. No response from
                    server
                  </p>
                ));
              }
            } catch (e) {
              console.error(e);
              toast.error(() => <p>Was unable to save the upsell!</p>);
            }
          }}
          pages={steps.length}
          previous={goPrevious}
        />
      </Box>
    </Box>
  );
}
